import { BasicAuthenticationClient } from '@frontend/authentication-v2';
import { Logger } from '@frontend/logger';
import { useModals } from '@frontend/modal';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../redux/store';
import { endUserSession } from '../redux/terminal-navigation-slice';
import { useKioskNavigate } from '../utils/kiosk-navigation';

interface ViewProps {
    showWarning: boolean;
    secondsRemaining: number;
    onLogout: () => void;
}

const useTokenExpirationGuard = (): ViewProps => {
    const dispatch = useAppDispatch();
    const navigate = useKioskNavigate();
    const { closeAllModals } = useModals();
    const navigationState = useAppSelector((state) => state.navigation);

    const [showWarning, setShowWarning] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(60);

    const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const tickIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Keep a ref to the jwt token so logout can safely read it from any closure
    const jwtToken = navigationState.user?.auth.jwt_token ?? null;
    const jwtTokenRef = useRef<string | null>(jwtToken);
    jwtTokenRef.current = jwtToken;

    const clearTimers = () => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (tickIntervalRef.current) {
            clearInterval(tickIntervalRef.current);
            tickIntervalRef.current = null;
        }
    };

    const performLogout = () => {
        clearTimers();
        setShowWarning(false);
        closeAllModals();
        if (jwtTokenRef.current) {
            BasicAuthenticationClient.logout(jwtTokenRef.current);
        }
        dispatch(endUserSession());
        navigate('/');
    };

    // Schedule or cancel the warning timer whenever the user token changes
    useEffect(() => {
        clearTimers();
        setShowWarning(false);

        if (!jwtToken) return;

        try {
            const decoded = jwtDecode<{ exp?: number }>(jwtToken);
            if (!decoded.exp) return;

            const expirationMs = decoded.exp * 1000;
            const msUntilWarning = expirationMs - 60 * 1000 - Date.now();

            if (msUntilWarning <= 0) {
                // Already inside the last-minute window — show warning immediately with actual remaining time
                const remaining = Math.ceil((expirationMs - Date.now()) / 1000);
                if (remaining > 0) {
                    setSecondsRemaining(remaining);
                    setShowWarning(true);
                }
            } else {
                warningTimerRef.current = setTimeout(() => {
                    setSecondsRemaining(60);
                    setShowWarning(true);
                }, msUntilWarning);
            }
        } catch (e) {
            Logger.error('Failed to decode user JWT for session expiration guard', e);
        }

        return () => {
            if (warningTimerRef.current) {
                clearTimeout(warningTimerRef.current);
                warningTimerRef.current = null;
            }
        };
    }, [jwtToken]);

    // Run countdown tick when the warning is visible
    useEffect(() => {
        if (!showWarning) return;

        tickIntervalRef.current = setInterval(() => {
            setSecondsRemaining((prev) => prev - 1);
        }, 1000);

        return () => {
            if (tickIntervalRef.current) {
                clearInterval(tickIntervalRef.current);
                tickIntervalRef.current = null;
            }
        };
    }, [showWarning]);

    // Auto-logout when the countdown reaches zero
    useEffect(() => {
        if (showWarning && secondsRemaining <= 0) {
            performLogout();
        }
    }, [secondsRemaining, showWarning]); // eslint-disable-line react-hooks/exhaustive-deps

    return { showWarning, secondsRemaining, onLogout: performLogout };
};

export default useTokenExpirationGuard;
